import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonsGroup from '../../../ui/ButtonsGroup';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../../app.config.vars/routes';
import Button from '../../../ui/Button';
import useInstitutionFormState from './useFormState';
import useFormRefs from './useFormRefs';
import { useValidator } from '../../../hooks/formValidator/useValidator';
import { useFocusOnFirstErrorField } from '../../../hooks/errorFieldFocus';
import FormFields from './formFields';
import useInstitutionValidationSchema from './useValidationSchema';
import { generateUpdatePayload, handleSubmitError, validateUpdatedFormFields } from '../helpers';
import { addInstitution, updateInstitution, } from '../../../api/admin/institutions';
import Loader from '../../../ui/Loader';
const InstitutionForm = ({ formType, institutionId }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { formState, updatedFormFields, setUpdatedFormFields, isStateReady } = useInstitutionFormState(institutionId);
    const { inputsValidationSchema } = useInstitutionValidationSchema({
        password: formState.password,
        formType,
    });
    const { formErrors, setFormErrors, validateAll, validateInputField } = useValidator({
        inputSchema: inputsValidationSchema,
    });
    const { refs, orderedRefs } = useFormRefs();
    const [submitStatus, setSubmitStatus] = useState(null);
    const [forceFocus, setForceFocus] = useState(false);
    useFocusOnFirstErrorField(formErrors, orderedRefs, forceFocus, setForceFocus);
    const returnToInstitutionsList = () => navigate(APP_ROUTES.institutions);
    const handleCreateInstitution = async () => {
        const errors = validateAll({
            inputValues: {
                name: formState.name,
                email: formState.email,
                password: formState.password,
                confirmPassword: formState.confirmPassword,
            },
        });
        if (Object.keys(errors).length > 0) {
            setForceFocus(true);
            return;
        }
        setSubmitStatus('pending');
        try {
            await addInstitution({
                institutionName: formState.name,
                email: formState.email,
                isActive: formState.isActive,
                password: formState.password,
                approvalStatus: formState.approvalStatus,
                ...(formState.approvalFile ? { approvalFile: formState.approvalFile } : {}),
                ...(formState.approvalMessage ? { approvalMessage: formState.approvalMessage } : {}),
                ...(formState.sendCredentialsEmail
                    ? { sendCredentialsEmail: true, emailLanguage: formState.emailLanguage }
                    : {}),
            });
            setSubmitStatus('success');
            returnToInstitutionsList();
        }
        catch (error) {
            handleSubmitError({ error, setSubmitStatus, setFormErrors, setForceFocus });
        }
    };
    const handleEditInstitution = async () => {
        if (updatedFormFields.size) {
            const updatedFieldsHasErrors = validateUpdatedFormFields({
                updatedFormFields,
                validateInputField,
                formState,
                unit: 'institution',
            });
            if (updatedFieldsHasErrors) {
                setForceFocus(true);
                return;
            }
            setSubmitStatus('pending');
            try {
                await updateInstitution(institutionId, generateUpdatePayload({
                    formState,
                    updatedFormFields,
                    unit: 'institution',
                }));
                setSubmitStatus('success');
                returnToInstitutionsList();
            }
            catch (error) {
                handleSubmitError({ error, setSubmitStatus, setFormErrors, setForceFocus });
            }
        }
    };
    return (_jsx("form", { className: 'super-admin-form institution-form', onSubmit: (event) => event.preventDefault(), children: isStateReady ? (_jsxs(_Fragment, { children: [_jsx(FormFields, { refs: refs, formState: formState, formErrors: formErrors, validateInputField: validateInputField, setUpdatedFormFields: setUpdatedFormFields, formType: formType }), _jsx("div", { className: "super-admin-form-submit", children: _jsxs(ButtonsGroup, { children: [_jsx(Button, { type: "submit", onClick: formType === 'create' ? handleCreateInstitution : handleEditInstitution, label: t(formType === 'create' ? 'button.create_institution' : 'button.edit_institution'), disabled: submitStatus === 'pending' ||
                                    (formType === 'edit' && updatedFormFields.size === 0) }), _jsx(Button, { variant: 'outlined', onClick: returnToInstitutionsList, label: t('button.cancel') })] }) })] })) : (_jsx(Loader, {})) }));
};
export default InstitutionForm;
